import React from 'react'
// libs
import { useSelector } from 'react-redux'
import { FieldArray, formValueSelector } from 'redux-form'
// components
import CustomColors from '../../../CustomColors'
import PlainSelect from '../../../PlainSelect'
import { ReduxInput, ReduxSwitch } from 'components/FormElements'
// constants
import { Forms } from 'constants/forms'
// schemas
import { ChartPalette, SchemaWidget, SCHEMA_KEYS } from 'schemas/chartConfig'
import { ChartSchemaNode, JsonSchemaType } from '../../schema'
// views
import { FieldHint } from '../../../../views'
// utils
import { isNumber, parseNumber, getEnumLabel, getFieldLabel, unwrapNullable, getSchemaValidator } from '../../utils'

const selector = formValueSelector(Forms.DASHBOARD_WIDGET_SETTINGS_FORM)

interface Props {
  name: string
  // The section the field sits in, e.g. chartConfig.display.colors: its other fields are read from there
  sectionName: string
  node: ChartSchemaNode
}

// One form control per schema property, picked by its type
const SchemaField: React.FC<Props> = ({ name, sectionName, node: schemaNode }) => {
  const node = unwrapNullable(schemaNode)
  const label = getFieldLabel(name, node)

  const isColors = node[SCHEMA_KEYS.widget] === SchemaWidget.Colors
  // The custom colors sit next to the palette and are used only by the CUSTOM one
  const palette = useSelector((state) => isColors && selector(state, `${sectionName}.palette`))

  // One function per field: redux-form registers a field again whenever its validate changes
  const validate = React.useMemo(() => getSchemaValidator(node), [schemaNode])

  if (isColors && palette !== ChartPalette.Custom) {
    return null
  }

  const getControl = () => {
    if (isColors) {
      return (
        <FieldArray
          name={name}
          component={CustomColors}
          props={{ label, maxItems: node.maxItems, pattern: node.items?.pattern }}
        />
      )
    }

    if (node.enum) {
      const options = node.enum.map((value) => ({ label: getEnumLabel(value), value: String(value) }))
      return <PlainSelect name={name} label={label} options={options} />
    }

    if (node.type === JsonSchemaType.Boolean) {
      return <ReduxSwitch name={name} label={label} />
    }

    if (isNumber(node)) {
      return (
        <ReduxInput
          name={name}
          label={label}
          type='number'
          inputProps={{ min: node.minimum, max: node.maximum, step: node.maximum === 1 ? 0.05 : 1 }}
          validate={validate}
          {...{ parse: parseNumber(!!node.nullable) }}
        />
      )
    }

    return <ReduxInput name={name} label={label} validate={validate} />
  }

  return (
    // The swatches speak for themselves: the description is for the AI that reads the schema
    <div title={isColors ? undefined : node.description}>
      {getControl()}
      {node.description && !isColors && <FieldHint>{node.description}</FieldHint>}
    </div>
  )
}

export default SchemaField
